import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { queryKeys } from "@/lib/queryClient";
import {
  driversApi,
  mockDrivers,
  mockPendingKYC,
  type DriversParams,
} from "../api/drivers.api";
import { APP_CONFIG } from "@/constants/config";
import type { PaginatedResponse, PendingDriverAPIResponse } from "@/types";

export function useDriverStates() {
  return useQuery({
    queryKey: ["drivers", "states"],
    queryFn: () => driversApi.getStates(),
    staleTime: 60 * 1000,
  });
}

export function useDrivers(params: DriversParams) {
  return useQuery({
    queryKey: queryKeys.drivers.list(params as Record<string, unknown>),
    queryFn: async (): Promise<PaginatedResponse<PendingDriverAPIResponse>> => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));

        let filtered = [...mockDrivers];

        if (params.search) {
          const search = params.search.toLowerCase();
          filtered = filtered.filter(
            (d) =>
              d.name.toLowerCase().includes(search) ||
              d.email.toLowerCase().includes(search) ||
              d.phone.includes(search) ||
              d.vehicleNumber.toLowerCase().includes(search),
          );
        }

        if (params.status && params.status !== "all") {
          filtered = filtered.filter((d) => d.status === params.status);
        }

        if (params.kycStatus && params.kycStatus !== "all") {
          filtered = filtered.filter((d) => d.kycStatus === params.kycStatus);
        }

        if (params.vehicleType && params.vehicleType !== "all") {
          filtered = filtered.filter(
            (d) => d.vehicleType === params.vehicleType,
          );
        }

        if (params.city && params.city !== "all") {
          filtered = filtered.filter((d) => d.city === params.city);
        }

        if (params.isOnline !== undefined) {
          filtered = filtered.filter((d) => d.isOnline === params.isOnline);
        }

        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const paginatedData = filtered.slice(start, start + limit);

        return {
          data: paginatedData,
          pagination: {
            page,
            limit,
            total: filtered.length,
            totalPages: Math.ceil(filtered.length / limit),
          },
        } as unknown as PaginatedResponse<PendingDriverAPIResponse>;
      }
      return driversApi.getAll(params);
    },
    staleTime: 30 * 1000,
  });
}

export function useDriver(id: string) {
  return useQuery({
    queryKey: queryKeys.drivers.detail(id),
    queryFn: async (): Promise<PendingDriverAPIResponse> => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const mock = mockDrivers.find((d) => d.id === id) || mockDrivers[0];
        // Cast mock to PendingDriverAPIResponse. Realistically we are not using mocks since the backend works, but for type consistency we cast or map it.
        return {
          id: mock.id,
          user_id: mock.id,
          license_number: "",
          license_expiry: "",
          aadhar_front_image: "",
          aadhar_back_image: "",
          driving_license_image: "",
          vehicle_rc_image: "",
          profile_image: mock.avatar || "",
          vehicle_id: mock.vehicleNumber,
          verification_status:
            mock.status === "active" ? "approved" : "pending",
          availability_status: mock.isOnline ? "online" : "offline",
          current_location: null,
          wallet_balance: mock.totalEarnings.toString(),
          average_rating: mock.rating,
          address: mock.city,
          total_trips: mock.totalTrips,
          created_at: mock.createdAt,
          updated_at: mock.createdAt,
          user: {
            id: mock.id,
            name: mock.name,
            phone: mock.phone,
            email: mock.email,
            role: "driver",
            is_verified: true,
            is_active: true,
            coins: 0,
            language_preference: "en",
            created_at: mock.createdAt,
            updated_at: mock.createdAt,
          },
        } as PendingDriverAPIResponse;
      }
      return driversApi.getById(id);
    },
    enabled: !!id,
  });
}

export function useDriverKYC(id: string) {
  return useQuery({
    queryKey: queryKeys.drivers.kyc(id),
    queryFn: async () => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return (
          mockPendingKYC.find((k) => k.driverId === id) || mockPendingKYC[0]
        );
      }
      return driversApi.getKYC(id);
    },
    enabled: !!id,
  });
}

export function usePendingKYC() {
  return useQuery({
    queryKey: ["drivers", "pending-kyc"],
    queryFn: async () => {
      return driversApi.getPendingKYC();
    },
    staleTime: 30 * 1000,
  });
}

export function useApproveDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return;
      }
      await driversApi.approve(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
      queryClient.invalidateQueries({ queryKey: ["drivers", "pending-kyc"] });
      toast.success("Driver approved successfully");
    },
  });
}

export function useRejectDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return;
      }
      await driversApi.reject(id, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
      queryClient.invalidateQueries({ queryKey: ["drivers", "pending-kyc"] });
      toast.success("Driver rejected");
    },
  });
}

export function useSuspendDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return;
      }
      await driversApi.suspend(id, reason);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.detail(id) });
      toast.success("Driver suspended");
    },
  });
}

export function useActivateDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return;
      }
      await driversApi.activate(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.detail(id) });
      toast.success("Driver activated");
    },
  });
}

export function useResetDriverData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => driversApi.resetDriverData(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
      toast.success("Driver data reset successfully");
    },
    onError: () => {
      toast.error("Failed to reset driver data");
    },
  });
}
